import React, { forwardRef, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import { registerLocale } from "react-datepicker";
import ptBR from "date-fns/locale/pt-BR";
import moment from "moment";
import "moment/locale/pt-br"; // traduz Moment

// 🔹 Configura Moment para PT-BR
moment.locale("pt-br");

// 🔹 Registra locale do calendário
registerLocale("pt-BR", ptBR);

/**
 * Props principais:
 * - label: string
 * - startDate, endDate: Date | null
 * - onChange: ( [start, end] ) => void
 * - nameStart, nameEnd: nomes para os inputs hidden (RHF)
 * - register: register do react-hook-form (opcional)
 * - required, disabled, readOnly, className, error
 * - dateFormat: default "dd/MM/yyyy"
 */
const DateRangeInput = ({
  className = "",
  label,
  startDate,
  endDate,
  onChange,
  nameStart = "startDate",
  nameEnd = "endDate",
  register, // opcional (react-hook-form)
  required = false,
  disabled = false,
  readOnly = false,
  error,
  dateFormat = "dd/MM/yyyy",
  ...props
}) => {
  const [focused, setFocused] = useState(false);

  const float = focused || !!startDate || !!endDate;

  // custom input com mesmo layout do seu <Input />
  const CustomInput = useMemo(
    () =>
      forwardRef(
        ({ value, onClick, onChange: _ignored, onBlur, onFocus }, ref) => (
          <div className={`relative ${className}`}>
            <input
              ref={ref}
              onClick={onClick}
              onFocus={(e) => {
                setFocused(true);
                onFocus && onFocus(e);
              }}
              onBlur={(e) => {
                setFocused(false);
                onBlur && onBlur(e);
              }}
              readOnly
              disabled={disabled}
              value={value || ""}
              placeholder={value ? "" : "Todas as datas"} // 🔹 Aqui entra o placeholder
              className={`block px-2.5 pb-2.5 pt-4 w-full text-sm text-gray-900 bg-transparent
            rounded-lg border border-gray-300 appearance-none
            dark:text-white dark:border-gray-600 dark:focus:border-blue-500
            focus:outline-none focus:ring-0
            ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}
            ${error ? "border-red-400" : ""}`}
            />

            <label
              className={`absolute text-md text-gray-500 dark:text-gray-400 duration-300 transform
          ${
            float
              ? "-translate-y-5 scale-75 top-2"
              : "-translate-y-1/2 top-1/2 scale-100"
          }
          bg-white dark:bg-gray-900 px-2 z-10 origin-[0] start-1 pointer-events-none`}
            >
              {label}
              {required ? " *" : ""}
            </label>
          </div>
        )
      ),
    [className, disabled, error, float, label, required]
  );

  // valores formatados para os hidden inputs (RHF)
  const startISO = startDate ? moment(startDate).format("YYYY-MM-DD") : "";
  const endISO = endDate ? moment(endDate).format("YYYY-MM-DD") : "";

  return (
    <>
      <DatePicker
        selectsRange
        startDate={startDate}
        endDate={endDate}
        onChange={(dates) => onChange?.(dates)}
        isClearable
        disabled={disabled}
        customInput={<CustomInput />}
        dateFormat={dateFormat}
        placeholderText="Todas as datas"
        locale="pt-BR"
        {...props}
      />

      {/* Integração com react-hook-form (opcional) */}
      {register && (
        <>
          <input
            type="hidden"
            {...register(nameStart, { required: false })}
            value={startISO}
            readOnly
          />
          <input
            type="hidden"
            {...register(nameEnd, { required: false })}
            value={endISO}
            readOnly
          />
        </>
      )}

      {error && <small className="text-red-400">{error}</small>}
    </>
  );
};

export default DateRangeInput;
